/**
 * MyProject — Investor "Single Source of Truth" view.
 *
 * Consumes GET /api/villas/{id} which now includes:
 *   summary.overall_progress_pct · phase_progress · project_health · next_milestone
 * Construction progress is NOT read from that summary: the general bar comes
 * from GET /api/construction-state and the villa bar from the villa's own
 * manual fields (see lib/constructionProgress).
 *
 * One villa at a time (pill selector when ≥2).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Section } from "@/pages/Shell";
import { api, API } from "@/lib/api";
import ConstructionMilestones, { BACKEND_KEY_TO_TKEY, VillaProgressBar } from "@/components/investor/ConstructionMilestones";
import { generalProgress, villaProgress } from "@/lib/constructionProgress";
import useInvestorLanguage from "@/i18n/useInvestorLanguage";
import ROISimulator from "@/pages/investor/ROISimulator";
import {
  House, Calendar, FileText, Buildings, CheckCircle, Clock, Warning,
  CaretRight, CurrencyEur, TrendUp, DownloadSimple, Camera, VideoCamera,
  Calculator,
} from "@phosphor-icons/react";

const ACCENT = "#2F3E35";
const HERO_IMG = "/brand/investor-project-hero.webp";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_KEYS = { sold: "my_project.status.sold", reserved: "my_project.status.reserved", available: "my_project.status.available" };

// Known backend payment.phase strings → translation key. Falls back to raw
// backend label when no match. Kept in sync with InvestorDashboard.jsx.
const PAYMENT_ROW_LABEL_MAP = {
  "Pago 1 · Reserva (10%)":            "payments.row.pago_1",
  "Pago 2 · Segundo pago (40%)":       "payments.row.pago_2",
  "Pago 3 · Tercer pago (30%)":        "payments.row.pago_3",
  "Pago 4 · Entrega de llaves (20%)":  "payments.row.pago_4",
};
function translatePaymentPhase(t, phase) {
  if (!phase) return "";
  const tkey = PAYMENT_ROW_LABEL_MAP[phase];
  if (tkey) {
    const tr = t(tkey);
    if (tr && tr !== tkey) return tr;
  }
  return phase;
}
const PHASE_KEYS = {
  foundation: "my_project.phase.foundation",
  structure: "my_project.phase.structure",
  finishes: "my_project.phase.finishes",
  delivery: "my_project.phase.delivery",
};

const HEALTH_CONFIG = {
  on_track:  { key: "my_project.health.on_track",  color: "#3F7D58", bg: "#E8F0E9", bgDark: "rgba(63,125,88,0.16)", icon: CheckCircle },
  attention: { key: "my_project.health.attention", color: "#B8860B", bg: "#FDF6E3", bgDark: "rgba(184,134,11,0.16)", icon: Warning },
  delayed:   { key: "my_project.health.delayed",   color: "#B23A3A", bg: "#FCEAEA", bgDark: "rgba(178,58,58,0.16)", icon: Warning },
};



export default function MyProject({ onNavigate }) {
  const navigate = useNavigate();
  const { t, lang } = useInvestorLanguage();
  const dateLocale = lang === "en" ? "en-US" : "es-ES";
  const [villas, setVillas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load investor's villas (backend filters to owned/payment villas)
  useEffect(() => {
    (async () => {
      try {
        const [v, n] = await Promise.all([
          api.get("/villas"),
          api.get("/payment-notifications/mine").catch(() => ({ data: [] })),
        ]);
        setVillas(v.data);
        setNotifs(n.data);
        if (v.data.length > 0) setSelectedId(v.data[0].villa_id);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load detail when selection changes
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/villas/${selectedId}`);
        setDetail(data);
      } catch (e) { console.error(e); }
    })();
  }, [selectedId]);

  if (loading) {
    return <Section title={t("my_project.title")}><div className="dv-card text-center py-16 text-stone-500 dark:text-stone-400">{t("my_project.loading")}</div></Section>;
  }

  if (villas.length === 0) {
    return (
      <div>
        <HeroWelcome t={t} />
        <Section title={t("my_project.title")}>
          <div className="dv-card text-center py-16">
            <House size={40} weight="light" className="mx-auto mb-3 text-stone-400 dark:text-stone-500" />
            <div className="font-serif-dv text-2xl mb-2">{t("my_project.no_villa")}</div>
            <div className="text-stone-500 dark:text-stone-400 text-sm max-w-md mx-auto">{t("my_project.no_villa_hint")}</div>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div>
      {villas.length >= 2 && (
        <VillaSelector villas={villas} selectedId={selectedId} onSelect={setSelectedId} />
      )}
      {detail ? (
        <VillaProjectView
          detail={detail}
          notifs={notifs}
          onOpenTab={onNavigate}
          onFullView={() => navigate(`/villa/${selectedId}`)}
          t={t}
          dateLocale={dateLocale}
        />
      ) : (
        <Section title={t("my_project.title")}><div className="dv-card text-center py-16 text-stone-500 dark:text-stone-400">{t("my_project.loading_detail")}</div></Section>
      )}
    </div>
  );
}


function HeroWelcome({ t }) {
  return (
    <div className="relative overflow-hidden rounded-xl mb-10 fade-up"
      style={{
        aspectRatio: "3.2/1", minHeight: 220,
        backgroundImage: `linear-gradient(90deg, rgba(47,62,53,0.9), rgba(47,62,53,0.35) 60%, transparent), url(${HERO_IMG})`,
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
      <div className="relative p-8 sm:p-12 text-white h-full flex flex-col justify-center">
        <div className="text-xs tracking-[0.3em] uppercase opacity-70 mb-2">{t("my_project.welcome_kicker")}</div>
        <h1 className="font-serif-dv text-4xl sm:text-5xl mb-3 max-w-lg">{t("my_project.welcome_title")}</h1>
        <p className="max-w-md opacity-80">{t("my_project.welcome_subtitle")}</p>
      </div>
    </div>
  );
}


function VillaSelector({ villas, selectedId, onSelect }) {
  return (
    <div className="flex gap-2 flex-wrap mb-6" data-testid="villa-selector">
      {villas.map(v => {
        const active = v.villa_id === selectedId;
        return (
          <button key={v.villa_id}
            data-testid={`villa-pill-${v.villa_id}`}
            onClick={() => onSelect(v.villa_id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              active ? "bg-[#2F3E35] text-white shadow-sm" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-200 hover:bg-stone-200 dark:hover:bg-stone-700"
            }`}
          >
            <House size={14} weight="light" className="inline mr-1.5 -mt-0.5" />
            {v.name}
          </button>
        );
      })}
    </div>
  );
}


function VillaProjectView({ detail, notifs, onOpenTab, onFullView, t, dateLocale }) {
  const { villa, summary, progress, payments, documents } = detail;
  const activeNotifFor = (pid) => notifs.find(n => n.payment_id === pid && n.status === "pending");
  const phaseLabel = (p) => t(PHASE_KEYS[p] || "") || p;
  const [downloadingReport, setDownloadingReport] = useState(false);

  // Handled explicitly instead of an <a target="_blank"> — on iOS Safari and
  // in-app browsers (Instagram/Facebook/WhatsApp), the fresh tab may not carry
  // the auth cookie and the PDF response 401s, resulting in a blank white
  // screen. Fetching the blob through the credentialed api client fixes it.
  const handleDownloadReport = async () => {
    if (downloadingReport) return;
    setDownloadingReport(true);
    let objectUrl = null;
    try {
      const res = await api.get(
        `/villas/${villa.villa_id}/project-report.pdf`,
        { responseType: "blob" }
      );
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `${villa?.name || "informe"}.pdf`;
      objectUrl = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.rel = "noopener";
      document.body.appendChild(link);
      try {
        link.click();
      } catch {
        // In-app browsers that block programmatic downloads fall back to
        // navigating to the blob so the PDF renders inline.
        window.location.href = objectUrl;
      }
      link.remove();
      setTimeout(() => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, 5000);
    } catch (e) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      window.alert(t("my_project.download_report_error"));
    } finally {
      setDownloadingReport(false);
    }
  };

  // Project-wide manual construction state — the GENERAL bar, identical for
  // every investor. The villa's own manual fields feed the separate villa
  // bar; neither one ever stands in for the other (lib/constructionProgress).
  const [globalState, setGlobalState] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/construction-state")
      .then(({ data }) => { if (!cancelled) setGlobalState(data || null); })
      .catch(() => { if (!cancelled) setGlobalState(null); });
    return () => { cancelled = true; };
  }, []);

  // Two independent readings. `pct === null` means nobody has registered a
  // figure yet: it renders «Pendiente de actualización», never a made-up 0%.
  const general = generalProgress(globalState);
  const villaOwn = villaProgress(villa);
  const phaseText = (key) => (key && BACKEND_KEY_TO_TKEY[key] ? t(BACKEND_KEY_TO_TKEY[key]) : null);

  const investedAmount = summary.payments_paid;
  // Use the investor-specific total (override → fallback to villa.price_eur →
  // last-resort sum of payments). Backend already resolves the first two via
  // summary.total_investment, so this stays a single safe expression.
  const totalPrice = summary.total_investment
    ?? villa.price_eur
    ?? (summary.payments_paid + summary.payments_pending);
  const investedPct = totalPrice > 0 ? Math.round((investedAmount / totalPrice) * 100) : 0;

  // "Último avance" KPI: the newest dated update of any kind — general,
  // this villa, or a published photo update.
  const lastUpdateAt =
    general.updatedAt
    || villaOwn.updatedAt
    || progress?.[0]?.created_at
    || summary?.recent_activity?.last_update_at
    || null;

  return (
    <div className="space-y-10 fade-up">
      {/* === A. Hero === */}
      <div className="relative overflow-hidden rounded-xl"
        style={{
          minHeight: 220,
          backgroundImage: `linear-gradient(90deg, rgba(47,62,53,0.92), rgba(47,62,53,0.4) 60%, transparent), url(${HERO_IMG})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}>
        <div className="relative p-8 sm:p-10 text-white flex flex-col h-full min-h-[220px] justify-between gap-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase opacity-70 mb-2">{t("my_project.your_project_kicker")}</div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-serif-dv text-4xl sm:text-5xl">{villa.name}</h1>
              <span className="px-3 py-1 rounded-full text-xs bg-white/20 backdrop-blur">
                {STATUS_KEYS[villa.status] ? t(STATUS_KEYS[villa.status]) : villa.status}
              </span>
            </div>
            <p className="opacity-80 text-sm mt-2">{villa.model} · Lombok</p>
          </div>
          <div className="self-start flex gap-2 flex-wrap">
            <button
              onClick={onFullView}
              data-testid="my-project-full-view-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 backdrop-blur text-white text-sm transition"
            >
              {t("my_project.full_view_btn")} <CaretRight size={14} />
            </button>
            <button
              type="button"
              onClick={handleDownloadReport}
              disabled={downloadingReport}
              data-testid="my-project-download-report-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-[#2F3E35] hover:-translate-y-0.5 text-sm font-medium transition-transform disabled:opacity-70 disabled:cursor-progress"
            >
              <DownloadSimple size={14} weight="bold" />
              {downloadingReport ? "…" : t("my_project.download_report")}
            </button>
          </div>
        </div>
      </div>

      {/* === Avance de obra — barra general del complejo (terracota, la misma
          para todos los inversores) y, debajo, la barra discreta de su villa === */}
      <div data-testid="my-project-global-progress">
        <ConstructionMilestones
          variant="compact"
          overallPct={general.pct}
          currentPhaseLabel={phaseText(general.phaseKey)}
          lastUpdateAt={general.updatedAt}
          globalLabel={t("milestones.global_label")}
          completedSuffix={t("milestones.completed_suffix")}
          currentPhaseTitle={t("milestones.current_phase")}
          lastUpdateTitle={t("milestones.last_update")}
          inProgressLabel={t("milestones.in_progress")}
          stepLabel={t("milestones.step")}
        />
        {general.note && (
          <div
            className="mt-3 text-sm text-stone-700 dark:text-stone-200 rounded border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-4 py-3"
            data-testid="progress-note-manual"
          >
            <div className="text-[11px] uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1">
              {t("my_project.progress_note_label")}
            </div>
            <div className="whitespace-pre-line">{general.note}</div>
          </div>
        )}
        <VillaProgressBar
          label={t("milestones.villa_label", { name: villa.name })}
          pct={villaOwn.pct}
          phaseLabel={phaseText(villaOwn.phaseKey)}
          lastUpdateAt={villaOwn.updatedAt}
          note={villaOwn.note}
          locale={dateLocale}
        />
      </div>

      {/* === KPIs row === */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={CurrencyEur} label={t("my_project.kpi.invested")}
          value={`€${Math.round(investedAmount).toLocaleString()}`}
          sub={totalPrice ? t("my_project.kpi.invested_of", { pct: investedPct, total: Math.round(totalPrice).toLocaleString() }) : ""}
          testid="kpi-invested"
        />
        <KpiCard
          icon={Calendar} label={t("my_project.kpi.pending")}
          value={`€${Math.round(summary.payments_pending).toLocaleString()}`}
          sub={(() => {
            const m = summary.next_milestone;
            if (m?.type !== "payment") return t("my_project.no_upcoming_payments");
            const d = m.days_until;
            if (d == null) return t("my_project.payment_scheduled");
            if (d < 0) return t("my_project.overdue_days", { n: Math.abs(d), plural: Math.abs(d) === 1 ? "" : "s" });
            if (d === 0) return t("my_project.due_today");
            if (d === 1) return t("my_project.due_tomorrow");
            return t("my_project.due_in", { n: d });
          })()}
          testid="kpi-pending"
        />
        <KpiCard
          icon={TrendUp} label={t("my_project.kpi.progress")}
          value={general.pct === null ? "—" : `${general.pct}%`}
          sub={general.pct === null ? t("milestones.pending_update") : t("my_project.kpi.complex_scope")}
          testid="kpi-overall"
        />
        <KpiCard
          icon={Clock} label={t("my_project.kpi.last_progress")}
          value={lastUpdateAt
            ? new Date(lastUpdateAt).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })
            : "—"}
          sub={progress[0] ? phaseLabel(progress[0].phase) : t("my_project.no_records")}
          testid="kpi-last-update"
        />
      </div>

      {/* === Project health === */}
      <ProjectHealthCard health={summary.project_health} t={t} />

      {/* Mini-card de rendimiento retirada intencionadamente de "Mi Proyecto"
          para no mostrar una proyección de ROI como si fuera una promesa dentro
          del resumen principal del inversor. El cálculo sigue disponible en el
          tab Rendimiento (Performance.jsx), que consume el mismo
          `summary.roi_tracking` sin cambios. */}

      {/* === Reportes de obra visibles (PR #7) === */}
      <ConstructionReportsSection villaId={villa.villa_id} t={t} onOpenTab={onOpenTab} />

      {/* === C. Next milestone === */}
      <Section title={t("my_project.next_milestone_title")} subtitle={t("my_project.next_milestone_subtitle")}>
        <NextMilestoneCard
          milestone={summary.next_milestone}
          activeNotif={summary.next_milestone?.payment_id ? activeNotifFor(summary.next_milestone.payment_id) : null}
          onGoPayments={() => onOpenTab && onOpenTab("payments")}
          t={t}
        />
      </Section>

      {/* === D. Timeline (últimos 10 eventos) — solo cuando hay algo real
          que mostrar (progress publicado, algún pago cobrado o documentos).
          Antes se renderizaba siempre y mostraba "Sin actividad reciente."
          para inversores recién onboarded. === */}
      {((progress?.length || 0) > 0
        || (payments || []).some(p => p.status === "paid")
        || (documents?.length || 0) > 0) && (
        <Section title={t("my_project.recent_activity_title")} subtitle={t("my_project.recent_activity_subtitle")}>
          <Timeline progress={progress} payments={payments} documents={documents} onFullView={onFullView} t={t} dateLocale={dateLocale} />
        </Section>
      )}

      {/* === E. Key documents — mismo criterio: solo si hay documentos.
          El tab Documentos completo sigue accesible desde la barra de tabs
          para el investor onboarding sin documentos. === */}
      {(documents?.length || 0) > 0 && (
        <Section title={t("my_project.recent_docs_title")} subtitle={t("my_project.recent_docs_subtitle")}>
          <KeyDocuments docs={documents.slice(0, 3)} onOpenDocs={() => onOpenTab && onOpenTab("documents")} t={t} dateLocale={dateLocale} />
        </Section>
      )}

      {/* === F. ROI simulator — recovered from the retired Performance tab.
          Sits at the very end as an expandable "what-if" tool. Bookings
          remains the source of real operational performance. === */}
      <RoiSimulatorSection t={t} />
    </div>
  );
}

function RoiSimulatorSection({ t }) {
  const [open, setOpen] = useState(false);
  const { lang } = useInvestorLanguage();
  const isEn = lang === "en";
  const copy = {
    title:      isEn ? "Profitability simulator" : "Simulador de rentabilidad",
    subtitle:   isEn
      ? "Simulate different occupancy and nightly-rate scenarios to estimate potential returns."
      : "Simula distintos escenarios de ocupación y tarifa para estimar el rendimiento potencial.",
    open:       isEn ? "Open simulator"  : "Abrir simulador",
    close:      isEn ? "Hide simulator"  : "Ocultar simulador",
    disclaimer: isEn
      ? "Illustrative simulation. Does not represent or guarantee future returns."
      : "Simulación orientativa. No representa ni garantiza rentabilidad futura.",
    bookings:   isEn
      ? "Real operational performance data lives in the Bookings section."
      : "El rendimiento real operativo vive en la sección Bookings.",
  };
  // Suppress unused-prop lint: `t` is threaded from the parent for potential
  // future i18n keys, but the section keeps its copy inline to stay a single
  // low-risk edit.
  void t;
  return (
    <div className="mt-8" data-testid="my-project-roi-simulator">
      <div className="dv-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
            <Calculator size={20} weight="light" className="text-stone-500 dark:text-stone-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs tracking-widest uppercase text-stone-500 dark:text-stone-400 mb-1">
              {copy.title}
            </div>
            <p className="text-sm text-stone-700 dark:text-stone-300">
              {copy.subtitle}
            </p>
            <p className="mt-1 text-[11px] text-stone-500 dark:text-stone-400">
              {copy.bookings}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="dv-btn dv-btn-primary shrink-0"
            data-testid="my-project-roi-toggle"
            aria-expanded={open}
          >
            {open ? copy.close : copy.open}
          </button>
        </div>
        {open && (
          <div className="mt-6" data-testid="my-project-roi-body">
            <div className="mb-3 rounded border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200">
              {copy.disclaimer}
            </div>
            <ROISimulator />
          </div>
        )}
      </div>
    </div>
  );
}


function KpiCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="dv-card" data-testid={testid} style={{ "--dv-accent": ACCENT }}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-xs tracking-wider uppercase text-stone-500 dark:text-stone-400">{label}</div>
        <Icon size={18} className="text-stone-400 dark:text-stone-500" weight="light" />
      </div>
      <div className="font-serif-dv text-3xl mb-1 dv-stat-value">{value}</div>
      <div className="text-xs text-stone-500 dark:text-stone-400">{sub}</div>
    </div>
  );
}


function ProjectHealthCard({ health, t }) {
  if (!health) return null;
  const cfg = HEALTH_CONFIG[health.status] || HEALTH_CONFIG.on_track;
  const Icon = cfg.icon;
  return (
    <div className="dv-health-card rounded-lg border p-5 flex items-start gap-4"
      data-testid={`health-${health.status}`}
      style={{ borderColor: cfg.color + "40", "--h-bg-l": cfg.bg, "--h-bg-d": cfg.bgDark }}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        style={{ background: cfg.color, color: "white" }}>
        <Icon size={20} weight="fill" />
      </div>
      <div className="flex-1">
        <div className="font-medium mb-1" style={{ color: cfg.color }}>{t("my_project.health_label")} · {t(cfg.key)}</div>
        <ul className="text-sm text-stone-700 dark:text-stone-200 space-y-0.5">
          {health.reasons.map((r, i) => <li key={i}>· {r}</li>)}
        </ul>
      </div>
    </div>
  );
}


function PhaseBar({ phase, weight, percentage, t }) {
  const done = percentage >= 100;
  const active = percentage > 0 && percentage < 100;
  return (
    <div data-testid={`phase-${phase}`}>
      <div className="flex justify-between items-center mb-1.5">
        <div className="text-sm">
          <span className="font-medium">{t(PHASE_KEYS[phase] || "") || phase}</span>
          <span className="text-stone-400 dark:text-stone-500 ml-2 text-xs">{t("my_project.phase_weight", { weight })}</span>
        </div>
        <div className="text-sm font-medium" style={{ color: done ? ACCENT : active ? "#B8860B" : "#A8A29E" }}>
          {percentage}%
        </div>
      </div>
      <div className="w-full h-2 bg-stone-100 dark:bg-stone-800 rounded-full overflow-hidden">
        <div className="h-full transition-all duration-500 rounded-full"
          style={{
            width: `${percentage}%`,
            background: done ? ACCENT : active ? "#B8860B" : "transparent",
          }} />
      </div>
    </div>
  );
}


function NextMilestoneCard({ milestone, activeNotif, onGoPayments, t }) {
  if (!milestone) {
    return (
      <div className="dv-card text-center py-10" data-testid="milestone-none">
        <CheckCircle size={36} weight="light" className="mx-auto text-green-600 mb-2" />
        <div className="font-serif-dv text-2xl mb-1">{t("my_project.on_track")}</div>
        <div className="text-sm text-stone-500 dark:text-stone-400">{t("my_project.no_milestones")}</div>
      </div>
    );
  }
  if (milestone.type === "payment") {
    const d = milestone.days_until;
    const daysStr =
      d == null ? t("my_project.no_date")
      : d < 0 ? t("my_project.overdue_days", { n: Math.abs(d), plural: Math.abs(d) === 1 ? "" : "s" })
      : d === 0 ? t("my_project.due_today")
      : d === 1 ? t("my_project.due_tomorrow")
      : t("my_project.due_in_long", { n: d });
    const danger = d != null && d < 0;
    return (
      <div className="dv-card flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        data-testid="milestone-payment">
        <div>
          <div className="text-xs tracking-wider uppercase text-stone-500 dark:text-stone-400 mb-1">{t("my_project.next_payment")}</div>
          <div className="font-serif-dv text-2xl">{translatePaymentPhase(t, milestone.label)}</div>
          <div className="text-sm text-stone-600 dark:text-stone-300 mt-1">
            €{Math.round(milestone.amount).toLocaleString()} {milestone.currency} · {t("my_project.due_label", { date: milestone.date })}
          </div>
          <div className={`text-xs mt-2 inline-block px-2 py-0.5 rounded ${
            danger ? "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300" : "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300"
          }`}>{daysStr}</div>
          {activeNotif && (
            <div className="text-xs text-amber-700 dark:text-amber-300 mt-2 inline-flex items-center gap-1 ml-2">
              · {t("my_project.notification_sent")}
            </div>
          )}
        </div>
        <button onClick={onGoPayments} data-testid="milestone-go-payments"
          className="dv-btn dv-btn-primary self-start sm:self-auto">
          {t("my_project.manage_payment")} <CaretRight size={14} />
        </button>
      </div>
    );
  }
  return (
    <div className="dv-card" data-testid="milestone-phase">
      <div className="text-xs tracking-wider uppercase text-stone-500 dark:text-stone-400 mb-1">{t("my_project.next_phase")}</div>
      <div className="font-serif-dv text-2xl">{milestone.label}</div>
      <div className="text-sm text-stone-500 dark:text-stone-400 mt-1">
        {t(PHASE_KEYS[milestone.phase] || "") || milestone.phase} · {t("my_project.currently_at", { pct: milestone.current_percentage })}
      </div>
    </div>
  );
}


function Timeline({ progress, payments, documents, onFullView, t, dateLocale }) {
  const events = useMemo(() => {
    const all = [
      ...progress.map(p => ({
        ts: p.created_at, kind: "progress",
        title: t("my_project.timeline.progress", { phase: t(PHASE_KEYS[p.phase] || "") || p.phase, pct: p.percentage }),
        subtitle: p.description || t("my_project.timeline.progress_default"),
      })),
      ...payments.filter(p => p.status === "paid").map(p => ({
        ts: p.paid_at || p.created_at, kind: "payment_paid",
        title: t("my_project.timeline.payment_paid", { phase: translatePaymentPhase(t, p.phase) }),
        subtitle: `€${Math.round(p.amount).toLocaleString()} ${p.currency}`,
      })),
      ...documents.map(d => ({
        ts: d.document_date || d.created_at, kind: "document",
        title: t("my_project.timeline.document", { title: d.title }),
        subtitle: d.doc_type || "",
      })),
    ].filter(e => e.ts);
    all.sort((a, b) => b.ts.localeCompare(a.ts));
    return all.slice(0, 10);
  }, [progress, payments, documents, t]);

  if (events.length === 0) {
    return <div className="dv-card text-center py-10 text-stone-500 dark:text-stone-400">{t("my_project.no_activity")}</div>;
  }

  const ICONS = {
    progress: Buildings, payment_paid: CurrencyEur, document: FileText,
  };

  return (
    <div className="dv-card p-0 overflow-hidden">
      <div className="divide-y divide-stone-100 dark:divide-stone-800">
        {events.map((e, i) => {
          const Icon = ICONS[e.kind] || Clock;
          return (
            <div key={i} className="px-5 py-4 flex items-start gap-4"
              data-testid={`timeline-event-${i}`}>
              <div className="w-9 h-9 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
                <Icon size={16} weight="light" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{e.title}</div>
                {e.subtitle && <div className="text-xs text-stone-500 dark:text-stone-400 truncate">{e.subtitle}</div>}
              </div>
              <div className="text-xs text-stone-400 dark:text-stone-500 shrink-0">
                {new Date(e.ts).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-stone-100 dark:border-stone-800 px-5 py-3 text-right">
        <button onClick={onFullView} data-testid="timeline-view-more"
          className="text-sm text-stone-600 dark:text-stone-300 hover:text-[#2F3E35] inline-flex items-center gap-1">
          {t("my_project.view_more")} <CaretRight size={12} />
        </button>
      </div>
    </div>
  );
}


function KeyDocuments({ docs, onOpenDocs, t, dateLocale }) {
  if (docs.length === 0) {
    return <div className="dv-card text-center py-8 text-stone-500 dark:text-stone-400 text-sm">{t("my_project.no_documents")}</div>;
  }
  return (
    <div className="space-y-3">
      {docs.map(d => (
        <div key={d.doc_id} className="dv-card flex items-center gap-4" data-testid={`key-doc-${d.doc_id}`}>
          <div className="w-10 h-10 rounded-md bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
            <FileText size={18} weight="light" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{d.title}</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">{d.doc_type} · {new Date(d.document_date || d.created_at).toLocaleDateString(dateLocale)}</div>
          </div>
          {(d.file_id || d.file_url) && (
            <a
              href={d.file_id ? `${BACKEND_URL}/api/files/${d.file_id}` : d.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="dv-btn dv-btn-ghost !p-2"
            ><CaretRight /></a>
          )}
        </div>
      ))}
      <button onClick={onOpenDocs} data-testid="key-docs-view-all"
        className="text-sm text-stone-600 dark:text-stone-300 hover:text-[#2F3E35] inline-flex items-center gap-1 ml-1">
        {t("my_project.view_all_docs")} <CaretRight size={12} />
      </button>
    </div>
  );
}



// ============================================================================
// PR #7 — Construction reports section (investor-side, read-only)
// Backend filters to visible_to_investor=true + this investor's scope. Hidden
// entirely when no reports are visible.
// ============================================================================
// Backend → label mapping for construction report categories. Kept in sync
// with PROJECT_WIDE_CATEGORIES in the constructor publish flow.
const REPORT_CATEGORY_LABELS = {
  general:             "General",
  preparacion_terreno: "Preparación del terreno",
  permisos:            "Permisos",
  cimentacion:         "Cimentación",
  estructura:          "Estructura",
  acabados:            "Acabados",
  entrega:             "Entrega",
  otro:                "Otro",
};

function ConstructionReportsSection({ villaId, t, onOpenTab }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/construction-reports", { params: villaId ? { villa_id: villaId } : {} })
      .then(({ data }) => { if (!cancelled) setReports(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setReports([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [villaId]);

  if (loading) return null;

  // "Mi Proyecto" is an executive summary — show only the most recent report.
  // The full timeline (with lightbox + video playback + project-wide updates)
  // lives in the "Avance de obra" tab. An empty state replaces the previous
  // silent null so a freshly-onboarded investor sees a clear message instead
  // of a missing section.
  if (!reports.length) {
    return (
      <Section
        title={t("my_project.latest_update_title")}
        subtitle={t("my_project.latest_update_subtitle")}
      >
        <div
          className="dv-card text-sm text-stone-600 dark:text-stone-300"
          data-testid="investor-reports-empty"
        >
          {t("my_project.empty_reports")}
        </div>
      </Section>
    );
  }

  const latestReports = reports.slice(0, 1);

  return (
    <Section
      title={t("my_project.latest_update_title")}
      subtitle={t("my_project.latest_update_subtitle")}
    >
      <div className="space-y-3">
        {latestReports.map((r) => {
          // Summary-only view: count photos / videos but don't render the
          // media inline. The full timeline (with lightbox + video playback)
          // lives in the "Avance de Obra" tab — we link to it instead.
          //
          // Prefer the enriched `photo_files` (mime allows photo/video
          // distinction) and fall back to legacy `photo_file_ids` (treated
          // as photos when no mime is available) + `photo_urls`.
          const enriched = Array.isArray(r.photo_files) ? r.photo_files : [];
          let photoCount = 0;
          let videoCount = 0;
          if (enriched.length > 0) {
            for (const pf of enriched) {
              if (typeof pf.mime === "string" && pf.mime.startsWith("video/")) videoCount += 1;
              else photoCount += 1;
            }
          } else if (Array.isArray(r.photo_file_ids)) {
            photoCount += r.photo_file_ids.length;
          }
          if (Array.isArray(r.photo_urls)) photoCount += r.photo_urls.length;
          const totalMedia = photoCount + videoCount;
          const categoryLabel = r.category ? (REPORT_CATEGORY_LABELS[r.category] || r.category) : null;
          return (
            <div key={r.report_id} className="dv-card" data-testid={`investor-report-${r.report_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {r.title}
                    {r.visibility_scope === "project_all" && (
                      <span
                        className="inline-flex text-[10px] px-2 py-0.5 rounded-full border bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-200 border-stone-200 dark:border-stone-700"
                        data-testid={`investor-report-project-all-${r.report_id}`}
                        title={t("my_project.project_wide_title")}
                      >
                        {t("my_project.project_wide_label")}
                      </span>
                    )}
                    {categoryLabel && (
                      <span
                        className="inline-flex text-[10px] px-2 py-0.5 rounded-full border bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800/60"
                        data-testid={`investor-report-category-${r.report_id}`}
                      >
                        {categoryLabel}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                    {r.reporting_period || t("my_project.no_period")} · {t("my_project.report_progress", { pct: r.progress_pct ?? 0 })}
                  </div>
                </div>
                {r.pdf_url && (
                  <a
                    href={r.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-3 py-1 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 inline-flex items-center gap-1"
                  >
                    {t("my_project.open_pdf")}
                  </a>
                )}
              </div>
              {r.summary && (
                <div className="text-sm text-stone-700 dark:text-stone-200 mt-3 whitespace-pre-line">{r.summary}</div>
              )}
              {totalMedia > 0 && (
                <div
                  className="mt-3 flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-stone-100 dark:border-stone-800"
                  data-testid={`investor-report-media-summary-${r.report_id}`}
                >
                  <div className="text-xs text-stone-600 dark:text-stone-300 flex items-center gap-3">
                    {photoCount > 0 && (
                      <span className="inline-flex items-center gap-1" data-testid={`investor-report-photo-count-${r.report_id}`}>
                        <Camera size={14} weight="light" />
                        {photoCount === 1
                          ? t("my_project.media_count.photo_one", { n: 1 })
                          : t("my_project.media_count.photo_many", { n: photoCount })}
                      </span>
                    )}
                    {videoCount > 0 && (
                      <span className="inline-flex items-center gap-1" data-testid={`investor-report-video-count-${r.report_id}`}>
                        <VideoCamera size={14} weight="light" />
                        {videoCount === 1
                          ? t("my_project.media_count.video_one", { n: 1 })
                          : t("my_project.media_count.video_many", { n: videoCount })}
                      </span>
                    )}
                  </div>
                  {onOpenTab && (
                    <button
                      type="button"
                      onClick={() => onOpenTab("progress")}
                      data-testid={`investor-report-view-progress-${r.report_id}`}
                      className="text-xs px-3 py-1 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 inline-flex items-center gap-1"
                    >
                      {t("my_project.view_in_progress")}
                      <CaretRight size={12} weight="bold" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {onOpenTab && (
        <div className="mt-3 text-right">
          <button
            type="button"
            onClick={() => onOpenTab("progress")}
            data-testid="investor-reports-view-all"
            className="text-xs text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-50 inline-flex items-center gap-1"
          >
            {t("my_project.view_all_in_progress")}
            <CaretRight size={12} weight="bold" />
          </button>
        </div>
      )}
    </Section>
  );
}
